"use client";

import { useState, useEffect, useMemo } from "react";
import styles from "../../Supplier Management/css/supplierManagement.module.css";
import Button from "../../../../components/Button";
import FileInput from "../../../../components/FileInput";
import MultiSelectDropdown from "../../../../components/MultiSelectDropdown";

export default function SupplierForm({
  isEditing,
  selectedSupplier,
  onSubmit,
  onCancel,
  onDisable,
  onEnable,
  saving,
  suppliers,
  setSuppliers,
  supplierCode,
  setSupplierCode,
  supplierName,
  setSupplierName,
  sapcLicense,
  setSapcLicense,
  sahpraLicense,
  setSahpraLicense,
  vatNumber,
  setVatNumber,
  address,
  setAddress,
  primaryContact,
  setPrimaryContact,
  email,
  setEmail,
  cellphone,
  setCellphone,
  altNumber,
  setAltNumber,
  categoriesSelected,
  setCategoriesSelected,
  file,
  setFile,
  documentName,
  setDocumentName,
  handleFileChange
}) {
  const isDisabledSupplier = (selectedSupplier?.status?.toLowerCase?.() || '') !== 'active' && !!selectedSupplier;

  useEffect(() => {
    if (isEditing && selectedSupplier && Array.isArray(selectedSupplier.category)) {
      setCategoriesSelected(selectedSupplier.category);
    }
  }, [isEditing, selectedSupplier, setCategoriesSelected]);

  // Build category options: include defaults + any existing supplier categories + current selections
  const categoryOptions = useMemo(() => {
    const defaults = ["Antibiotics", "Painkillers", "Vitamins"];
    const existing = Array.isArray(selectedSupplier?.category) ? selectedSupplier.category : [];
    const dynamic = Array.isArray(categoriesSelected) ? categoriesSelected : [];
    const all = [...defaults, ...existing, ...dynamic]
      .filter(Boolean)
      .map((s) => String(s).trim());
    return Array.from(new Set(all));
  }, [selectedSupplier, categoriesSelected]);

  return (
    <>
      <div className={styles.headerRow}>
        <h1 className={styles.title}>{isEditing ? 'Edit Supplier' : 'New Supplier'}</h1>
      </div>

      <form className={styles.formCard} onSubmit={onSubmit}>
        <div className={styles.formGrid}>
          {/* Row 1: 5 fields */}
          <div className={styles.field}>
            <label className={styles.label}>Supplier Code</label>
            <input className={styles.input} placeholder="AB305" value={supplierCode} onChange={(e) => setSupplierCode(e.target.value)} disabled={isDisabledSupplier} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Supplier Name</label>
            <input className={styles.input} value={supplierName} onChange={(e) => setSupplierName(e.target.value)} disabled={isDisabledSupplier} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>SAPC License Number</label>
            <input className={styles.input} value={sapcLicense} onChange={(e) => setSapcLicense(e.target.value)} disabled={isDisabledSupplier} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>SAHPRA License Number</label>
            <input className={styles.input} value={sahpraLicense} onChange={(e) => setSahpraLicense(e.target.value)} disabled={isDisabledSupplier} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>VAT Number</label>
            <input className={styles.input} value={vatNumber} onChange={(e) => setVatNumber(e.target.value)} disabled={isDisabledSupplier} />
          </div>

          {/* Row 2: 4 fields (leave one spacer) */}
          <div className={styles.field}>
            <label className={styles.label}>Address</label>
            <input className={styles.input} value={address} onChange={(e) => setAddress(e.target.value)} disabled={isDisabledSupplier} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Primary Contact Name</label>
            <input
              className={styles.input}
              value={primaryContact}
              onChange={(e) => {
                const raw = e.target.value || "";
                const lettersOnly = raw.replace(/[^a-zA-Z\s]/g, "");
                setPrimaryContact(lettersOnly);
              }}
              disabled={isDisabledSupplier}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Email Address</label>
            <input className={styles.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={isDisabledSupplier} />
          </div>
          <div className={styles.field}>
            <label className={styles.label}>Cellphone Number</label>
            <input
              className={styles.input}
              value={cellphone}
              inputMode="numeric"
              maxLength={10}
              onChange={(e) => {
                const val = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
                setCellphone(val);
              }}
              disabled={isDisabledSupplier}
            />
          </div>
          {/* Move Alternative Number next to Cellphone in the same row */}
          <div className={`${styles.field} ${styles.altField}`}>
            <label className={styles.label}>Alternative Number</label>
            <input
              className={styles.input}
              value={altNumber}
              inputMode="numeric"
              maxLength={10}
              onChange={(e) => {
                const val = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
                setAltNumber(val);
              }}
              disabled={isDisabledSupplier}
            />
          </div>

          {/* Row 3: Category multi-select (left), Alternative Number (right) */}
          <div className={styles.field}>
            <MultiSelectDropdown
              label="Category"
              name="supplierCategories"
              options={categoryOptions}
              value={categoriesSelected}
              onChange={setCategoriesSelected}
              placeholder="Select categories"
              maxVisible={3}
              compact
              disabled={isDisabledSupplier}
            />
          </div>
          {/* remove extra spacers since Alternative Number moved up */}
        </div>

        <div className={styles.uploadSection}>
          <strong>Upload Supporting Documents</strong>
          <div className={styles.uploadRow} style={{ flexDirection: 'column', gap: '0.75rem' }}>
            <FileInput
              label=""
              name="supplierDoc"
              id="supplierDoc"
              value={file}
              onChange={handleFileChange}
              accept=".pdf,.jpg,.png,.jpeg,.doc,.docx"
              className={styles.fileInput}
              wide
              disabled={isDisabledSupplier}
              existingUrl={selectedSupplier?.docurl}
            />
            <div className={styles.field}>
              <label className={styles.label} htmlFor="supplierDocName">Document Name</label>
              <input id="supplierDocName" className={styles.input} placeholder="e.g. Tax Clearance, SAPC Cert" value={documentName} onChange={(e) => setDocumentName(e.target.value)} disabled={isDisabledSupplier} />
            </div>
          </div>
        </div>

        <div className={styles.actionsRow}>
          {!isDisabledSupplier && <Button variant="primary" noHover disabled={!!saving}>Save</Button>}
          {isEditing && !isDisabledSupplier && (
            <Button
              variant="primary"
              className={styles.dangerBtn}
              noHover
              onClick={(e) => {
                e.preventDefault();
                if (onDisable) onDisable();
              }}
            >
              Disable
            </Button>
          )}
          {/* <Button 
            variant="primary" 
            noHover 
            className={styles.cancelBtn} 
            onClick={(e) => { 
              e.preventDefault(); 
              onCancel(); 
            }}
          >Back</Button> */}
        </div>
      </form>
    </>
  );
}
